import moment from "moment";

import { DomainType, GridCodeType, addErrorsToDataItemValidations } from "../models";
import { EncounterCategory } from "../models/encounterServiceEntity";
import { modifierIsInList } from "../reducers/Helpers/encounterResearchPane";
import {
  findGroupingResultByViewId,
  findGroupingSource,
  isActiveGrouperGpcsBased,
} from "../services/encounter/encounterMapping";

import type {
  EncounterEntity,
  PartialFacilityPreferences,
  ResequenceModes,
  ResequenceableGridType,
  ValidationErrors,
} from "../models";
import type { BaseCodeRow, CodeGroup } from "../models/codeGrid";
import type { FacilityPreferences } from "../models/facilitySettings";
import type { IdDescriptionBase } from "../models/patientEncounter";
import type { ValuesRow } from "../models/valuesGrid";
import type { RemoteServicesSettingsObject } from "../scenes/SystemPreferences/models/systemPreferences";

export const combineEncounterCodes = (encounter: EncounterEntity): BaseCodeRow[] =>
  encounter.diagnoses.codes.concat(
    encounter.inProcedures.codes,
    encounter.outProcedures.codes,
    encounter.visitReasons.codes,
    encounter.professionalProcedures.codes,
  );

export const getCodesByGridType = (
  type: GridCodeType,
  encounter: EncounterEntity,
): BaseCodeRow[] => {
  switch (type) {
    case GridCodeType.OUTPROCEDURES: {
      return encounter.outProcedures.codes;
    }

    case GridCodeType.DIAGNOSES: {
      return encounter.diagnoses.codes;
    }

    case GridCodeType.INPROCEDURES: {
      return encounter.inProcedures.codes;
    }

    case GridCodeType.VISITREASONS: {
      return encounter.visitReasons.codes;
    }

    case GridCodeType.PROFESSIONAL_PROCEDURES: {
      return encounter.professionalProcedures.codes;
    }

    default: {
      return [];
    }
  }
};

export const addErrorsToGridFirstRow = (
  gridData: ValuesRow[] | BaseCodeRow[],
  errors: ValidationErrors,
  fieldName: string,
  proposedLevel = "Critical",
): ValuesRow[] | BaseCodeRow[] => {
  const data: any[] = [];
  for (let ind = 0, len = gridData.length; ind < len; ind++) {
    if (ind === 0) {
      const validations = gridData[0].Validations ? [...gridData[0].Validations] : [];
      addErrorsToDataItemValidations(validations, fieldName, errors, proposedLevel);

      data.push({
        ...gridData[ind],
        Validations: validations,
      });
    } else {
      data.push(gridData[ind]);
    }
  }

  return data;
};

export const addValidationErrors = (
  gridData: ValuesRow[] | BaseCodeRow[] = [],
  generalErrors: ValidationErrors = "",
): ValuesRow[] | BaseCodeRow[] => addErrorsToGridFirstRow(gridData, generalErrors, "Code");

export const resequenceCodeGroup = (
  inProgress: EncounterEntity,
  gridCodeType: GridCodeType,
  order?: string[],
): BaseCodeRow[] => {
  const codes = getCodesByGridType(gridCodeType, inProgress);
  if (!order?.length) {
    return codes;
  }

  const oldCodes = [...codes];
  const newCodes: BaseCodeRow[] = [];
  // place all reordered codes in the necessary order
  for (let ind = 0, len = order.length; ind < len; ind++) {
    let found = false;
    for (let cind = 0, clen = oldCodes.length; cind < clen; cind++) {
      if (oldCodes[cind].id === order[ind]) {
        newCodes.push(oldCodes[cind]);
        oldCodes.splice(cind, 1);
        found = true;
        break;
      }
    }

    if (!found) {
      console.log(`Code with ViewId=${order[ind]} not found in ${gridCodeType}`);
    }
  }

  // place all not found codes
  for (let rind = 0, rlen = oldCodes.length; rind < rlen; rind++) {
    newCodes.push(oldCodes[rind]);
  }

  return newCodes;
};

// All grids can be auto-resequenced
export const AUTORESEQUENCE_ALL = {
  [GridCodeType.DIAGNOSES]: true,
  [GridCodeType.INPROCEDURES]: true,
  [GridCodeType.OUTPROCEDURES]: true,
  [GridCodeType.PROFESSIONAL_PROCEDURES]: true,
};

// https://trucode.atlassian.net/browse/GUI-2900?focusedCommentId=244904
// Use Primary groupings only
//  For OP there should be only OutProceduresOrder, for IP - DiagnosesOrder and InProceduresOrder
export const resequenceCodes = (
  inProgress: EncounterEntity,
  modes: ResequenceModes = AUTORESEQUENCE_ALL,
): EncounterEntity => {
  const primaryIPGrouping = findGroupingSource(true, inProgress);
  const primaryIPResult =
    primaryIPGrouping ? findGroupingResultByViewId(inProgress, primaryIPGrouping.ViewId) : null;
  const primaryOPGrouping = findGroupingSource(false, inProgress);
  const primaryOPResult =
    primaryOPGrouping ? findGroupingResultByViewId(inProgress, primaryOPGrouping.ViewId) : null;

  const newDiagnoses =
    modes[GridCodeType.DIAGNOSES] ?
      resequenceCodeGroup(inProgress, GridCodeType.DIAGNOSES, primaryIPResult?.DiagnosesOrder)
    : inProgress.diagnoses.codes;

  const newInProcedures =
    modes[GridCodeType.INPROCEDURES] ?
      resequenceCodeGroup(inProgress, GridCodeType.INPROCEDURES, primaryIPResult?.InProceduresOrder)
    : inProgress.inProcedures.codes;

  const newOutProcedures =
    modes[GridCodeType.OUTPROCEDURES] ?
      resequenceCodeGroup(
        inProgress,
        GridCodeType.OUTPROCEDURES,
        primaryOPResult?.OutProceduresOrder,
      )
    : inProgress.outProcedures.codes;

  // Professional procedures use PFS results (separate from OP grouping)
  const newProfessionalProcedures =
    modes[GridCodeType.PROFESSIONAL_PROCEDURES] ?
      resequenceCodeGroup(
        inProgress,
        GridCodeType.PROFESSIONAL_PROCEDURES,
        inProgress.ProcessPFSResult?.ProceduresOrder,
      )
    : inProgress.professionalProcedures.codes;

  const newInProgress = {
    ...inProgress,
    diagnoses: { ...inProgress.diagnoses, codes: newDiagnoses },
    inProcedures: { ...inProgress.inProcedures, codes: newInProcedures },
    outProcedures: { ...inProgress.outProcedures, codes: newOutProcedures },
    professionalProcedures: {
      ...inProgress.professionalProcedures,
      codes: newProfessionalProcedures,
    },
  };

  return newInProgress;
};

export const needResequenceEncounterCodes = (
  inProgress: EncounterEntity,
  resequenceModes: ResequenceModes = {},
): boolean => {
  const primaryIPGrouping = findGroupingSource(true, inProgress);
  const primaryIPResult =
    primaryIPGrouping ? findGroupingResultByViewId(inProgress, primaryIPGrouping.ViewId) : null;
  const primaryOPGrouping = findGroupingSource(false, inProgress);
  const primaryOPResult =
    primaryOPGrouping ? findGroupingResultByViewId(inProgress, primaryOPGrouping.ViewId) : null;

  if (resequenceModes[GridCodeType.DIAGNOSES] && primaryIPResult?.DiagnosesOrder?.length) {
    return true;
  }
  if (resequenceModes[GridCodeType.INPROCEDURES] && primaryIPResult?.InProceduresOrder?.length) {
    return true;
  }
  if (resequenceModes[GridCodeType.OUTPROCEDURES] && primaryOPResult?.OutProceduresOrder?.length) {
    return true;
  }
  if (
    resequenceModes[GridCodeType.PROFESSIONAL_PROCEDURES] &&
    inProgress.ProcessPFSResult?.ProceduresOrder?.length
  ) {
    return true;
  }

  return false;
};

// Generic resequence function that works with resequenceable grid types
export const resequenceGrid = (
  inProgress: EncounterEntity,
  gridCodeType: ResequenceableGridType,
): EncounterEntity => {
  switch (gridCodeType) {
    case GridCodeType.DIAGNOSES: {
      const primaryIPGrouping = findGroupingSource(true, inProgress);
      const primaryIPResult =
        primaryIPGrouping ? findGroupingResultByViewId(inProgress, primaryIPGrouping.ViewId) : null;

      const newDiagnoses = resequenceCodeGroup(
        inProgress,
        GridCodeType.DIAGNOSES,
        primaryIPResult?.DiagnosesOrder,
      );

      return {
        ...inProgress,
        diagnoses: { ...inProgress.diagnoses, codes: newDiagnoses },
      };
    }
    case GridCodeType.INPROCEDURES: {
      const primaryIPGrouping = findGroupingSource(true, inProgress);
      const primaryIPResult =
        primaryIPGrouping ? findGroupingResultByViewId(inProgress, primaryIPGrouping.ViewId) : null;

      const newInProcedures = resequenceCodeGroup(
        inProgress,
        GridCodeType.INPROCEDURES,
        primaryIPResult?.InProceduresOrder,
      );

      return {
        ...inProgress,
        inProcedures: { ...inProgress.inProcedures, codes: newInProcedures },
      };
    }
    case GridCodeType.OUTPROCEDURES: {
      const primaryOPGrouping = findGroupingSource(false, inProgress);
      const primaryOPResult =
        primaryOPGrouping ? findGroupingResultByViewId(inProgress, primaryOPGrouping.ViewId) : null;

      const newOutProcedures = resequenceCodeGroup(
        inProgress,
        GridCodeType.OUTPROCEDURES,
        primaryOPResult?.OutProceduresOrder,
      );

      return {
        ...inProgress,
        outProcedures: { ...inProgress.outProcedures, codes: newOutProcedures },
      };
    }
    case GridCodeType.PROFESSIONAL_PROCEDURES: {
      const newProfessionalProcedures = resequenceCodeGroup(
        inProgress,
        GridCodeType.PROFESSIONAL_PROCEDURES,
        inProgress.ProcessPFSResult?.ProceduresOrder,
      );

      return {
        ...inProgress,
        professionalProcedures: {
          ...inProgress.professionalProcedures,
          codes: newProfessionalProcedures,
        },
      };
    }
    default:
      return inProgress;
  }
};

// Generic need resequence function that works with resequenceable grid types
export const needResequenceGrid = (
  inProgress: EncounterEntity,
  gridCodeType: ResequenceableGridType,
): boolean => {
  switch (gridCodeType) {
    case GridCodeType.DIAGNOSES: {
      const primaryIPGrouping = findGroupingSource(true, inProgress);
      const primaryIPResult =
        primaryIPGrouping ? findGroupingResultByViewId(inProgress, primaryIPGrouping.ViewId) : null;

      return !!primaryIPResult?.DiagnosesOrder?.length;
    }
    case GridCodeType.INPROCEDURES: {
      const primaryIPGrouping = findGroupingSource(true, inProgress);
      const primaryIPResult =
        primaryIPGrouping ? findGroupingResultByViewId(inProgress, primaryIPGrouping.ViewId) : null;

      return !!primaryIPResult?.InProceduresOrder?.length;
    }
    case GridCodeType.OUTPROCEDURES: {
      const primaryOPGrouping = findGroupingSource(false, inProgress);
      const primaryOPResult =
        primaryOPGrouping ? findGroupingResultByViewId(inProgress, primaryOPGrouping.ViewId) : null;

      return !!primaryOPResult?.OutProceduresOrder?.length;
    }
    case GridCodeType.PROFESSIONAL_PROCEDURES: {
      return !!inProgress.ProcessPFSResult?.ProceduresOrder?.length;
    }
    default:
      return false;
  }
};

export const getProfessionalProceduresAutoResequence = (
  inProgress: EncounterEntity,
  preferences?: FacilityPreferences,
): boolean | undefined => {
  if (!preferences) {
    return undefined;
  }

  if (inProgress.domain === DomainType.Practice) {
    if (!preferences.ProfessionalPreferences) {
      return undefined;
    }

    return preferences.ProfessionalPreferences.EnableAutomaticCPTCodeResequencingByTotalRVU;
  }

  if (inProgress.ValidationResult?.Category === EncounterCategory.Inpatient) {
    if (!preferences.InpatientPreferences) {
      return undefined;
    }

    return preferences.InpatientPreferences.ResequenceCptByTotalFacilityRvu;
  }

  if (!preferences.OutpatientPreferences) {
    return undefined;
  }

  return preferences.OutpatientPreferences.ResequenceCptByTotalFacilityRvu;
};

export const getResequenceModes = (
  inProgress: EncounterEntity,
  preferences?: FacilityPreferences,
): ResequenceModes => {
  if (!preferences) {
    return {};
  }

  const icdAutoResequence = preferences.InpatientPreferences?.AutomaticallyResequence ?? false;
  const cptAutoResequence = preferences.OutpatientPreferences?.AutomaticallyResequence ?? false;

  // Practice uses Professional Preferences; facility IP/OP keep Total Facility RVU settings
  const proAutoResequence =
    getProfessionalProceduresAutoResequence(inProgress, preferences) ?? false;

  return {
    // ICD codes (DX and PCS) ONLY resequence when IP setting is checked
    // This applies to ALL encounter types (IP, OP, ASC)
    [GridCodeType.DIAGNOSES]: icdAutoResequence,
    [GridCodeType.INPROCEDURES]: icdAutoResequence,

    // CPT codes ONLY resequence when OP setting is checked
    // This applies to OP and ASC encounters
    [GridCodeType.OUTPROCEDURES]: cptAutoResequence,

    // Professional CPT codes use RVU-based resequencing (separate PFS system)
    [GridCodeType.PROFESSIONAL_PROCEDURES]: proAutoResequence,
  };
};

export const getAutoResequenceModes = (
  inProgress: EncounterEntity,
  preferences?: FacilityPreferences,
): ResequenceModes => {
  const modes = getResequenceModes(inProgress, preferences);
  // Filter to only include grids where auto-resequencing is enabled (true)
  const autoModes: ResequenceModes = {};

  Object.keys(modes).forEach((gridType) => {
    if (modes[gridType] === true) {
      autoModes[gridType] = true;
    }
  });

  return autoModes;
};

export const getManualResequenceModes = (
  inProgress: EncounterEntity,
  preferences?: FacilityPreferences,
): ResequenceModes => {
  const modes = getResequenceModes(inProgress, preferences);
  const manualModes: ResequenceModes = {};

  // Manual resequencing is available when auto-resequencing is disabled
  Object.keys(modes).forEach((gridType) => {
    if (modes[gridType] === false) {
      manualModes[gridType] = true;
    }
  });

  return manualModes;
};

export const getResequenceModeForGrid = (
  gridType: GridCodeType,
  inProgress: EncounterEntity,
  preferences?: FacilityPreferences,
): boolean | undefined => getResequenceModes(inProgress, preferences)[gridType];

export const getAutoResequenceModeForGrid = (
  gridType: GridCodeType,
  inProgress: EncounterEntity,
  preferences?: FacilityPreferences,
): boolean => getResequenceModes(inProgress, preferences)[gridType] === true;

export const getManualResequenceModeForGrid = (
  gridType: GridCodeType,
  inProgress: EncounterEntity,
  preferences?: FacilityPreferences,
): boolean => getResequenceModes(inProgress, preferences)[gridType] === false;

export const resequenceHasValues = (modes: ResequenceModes = {}): boolean =>
  Object.keys(modes).some((gridType) => !!modes[gridType]);

export const compareNoCase = (first?: string, second?: string): boolean =>
  (first || "").toUpperCase() === (second || "").toUpperCase();

export const getBaseDate = (encounter: EncounterEntity, onlyDate = false): string => {
  const baseDate = encounter.ValidationResult?.BaseDate || "";
  return onlyDate ? baseDate.split("T")[0] : baseDate;
};

// encounterType for codebooks: provide IP or OP (additional encounter types to be added)
// TODO: Add logic for additional encounter types when available
export const getEncounterTypeForCodebooks = (encounter: EncounterEntity): string => {
  let type = "";
  switch (encounter.ValidationResult?.Category) {
    case "Inpatient":
      type = "IP";
      break;
    case "Outpatient":
      type = "OP";
      break;
    case "Professional":
      type = "PRO";
      break;
    default:
      break;
  }

  return type;
};

// facility for codebooks
export const getFacilityForCodebooks = (
  encounter: EncounterEntity,
  facilities: IdDescriptionBase[],
): string => {
  const facility = facilities.find((rec) => rec.ViewId === encounter.facility);
  return facility?.id || "";
};

export const isDiagnosesAnalysisActive = (
  isShowingPdxAnalysis?: boolean,
  isShowingEccAnalysis?: boolean,
): boolean => isShowingPdxAnalysis === true || isShowingEccAnalysis === true;

export const isEccAnalysisAvailable = (encounter: EncounterEntity): boolean => {
  const isFacilityDomain = encounter.domain === DomainType.Facility;
  const isInpatient = encounter.ValidationResult?.Category === EncounterCategory.Inpatient;

  return isFacilityDomain && isInpatient;
};

export const isEccDisabled = (encounter: EncounterEntity): boolean => {
  const Date20151001 = moment("2015-10-01");
  const encounterBaseDate = getBaseDate(encounter);
  const encounterIsBefore20151001 =
    encounterBaseDate && moment(encounterBaseDate).isBefore(Date20151001);

  return (
    encounter.diagnoses.codes.length < 2 ||
    (encounter.diagnoses.codes.length === 2 && encounter.diagnoses.codes[1].empty) ||
    !!encounterIsBefore20151001
  );
};

export const isPdxDisabled = (
  encounter: EncounterEntity,
  remoteServicesSettings: RemoteServicesSettingsObject,
): boolean => {
  const Date20151001 = moment("2015-10-01");
  const encounterBaseDate = getBaseDate(encounter);
  const encounterIsBefore20151001 =
    encounterBaseDate && moment(encounterBaseDate).isBefore(Date20151001);
  const disablePdxForGpcs =
    (remoteServicesSettings.LimitGpcsTransactions && remoteServicesSettings.DisablePdxForGpcs) ??
    false;

  return (
    encounter.diagnoses.codes.length < 2 ||
    (encounter.diagnoses.codes.length === 2 && encounter.diagnoses.codes[1].empty) ||
    encounterIsBefore20151001 ||
    (disablePdxForGpcs && isActiveGrouperGpcsBased(encounter))
  );
};

export const isEncounterClosedError = (errorMessage: string | undefined): boolean => {
  if (errorMessage) {
    return /^Session for.*is not found$/.exec(errorMessage) !== null;
  }
  return false;
};

const getEncounterStorageKey = (id: string): string => `TC_ENCOUNTER_LAST_ACCESS_TIME_${id}`;

export const updateEncounterLastAccessTime = (id: string): void => {
  const key = getEncounterStorageKey(id);
  if (sessionStorage.getItem(key) === "Outdated") {
    return;
  }

  const now = moment().format("YYYY-MM-DD HH:mm:ss");
  sessionStorage.setItem(key, now);
};

const getEncounterLastAccessTime = (id: string): string | null => {
  const key = getEncounterStorageKey(id);
  return sessionStorage.getItem(key);
};

export const isEncounterOutdated = (id: string): boolean => {
  const last = getEncounterLastAccessTime(id);
  if (!last || last === "Outdated") {
    return true;
  }
  const lastTime = moment(last);

  const diff = moment().diff(lastTime, "minutes");
  return diff > 1;
};

export const markEncounterOutdated = (id: string): void => {
  const key = getEncounterStorageKey(id);
  sessionStorage.setItem(key, "Outdated");
};

export const clearEncounterLastAccessTime = (id: string): void => {
  const key = getEncounterStorageKey(id);
  sessionStorage.setItem(key, "");
};

// prepare partial preferences from full preferences
export const preparePartialFacilityPreferences = (
  inProgress: EncounterEntity,
  facilityPreferences?: FacilityPreferences,
): PartialFacilityPreferences => ({
  // return this preference only for inpatient
  InPatientAutofillPOAForExemptDiagnosisCodes:
    inProgress.ValidationResult?.Category === EncounterCategory.Inpatient ?
      facilityPreferences?.InpatientPreferences?.AutofillPOAForExemptDiagnosisCodes
    : false,
});

// update inProgress according with partial preferences
export const applyPartialFacilityPreferences = (
  inProgress: EncounterEntity,
  partialFacilityPreferences?: PartialFacilityPreferences,
): boolean => {
  let updated = false;

  // auto clear exempt poa
  if (
    partialFacilityPreferences?.InPatientAutofillPOAForExemptDiagnosisCodes &&
    inProgress.ValidationResult?.Category === EncounterCategory.Inpatient
  ) {
    const gridCodes = inProgress.diagnoses.codes;
    const len = gridCodes.length;

    for (let ind = 0; ind < len; ind++) {
      const code = gridCodes[ind];

      // clear presentOnAdmission in each code and mark the update
      if (code.exemptPoa !== undefined && code.presentOnAdmission) {
        delete code.presentOnAdmission;
        code.autoChanged = code.autoChanged || [];
        code.autoChanged.push("presentOnAdmission");
        updated = true;
      }
    }

    if (updated) {
      console.log("Exempt POA were cleared");
    }
  }

  return updated;
};

export const findCodeInCodeGroupByCode = (
  codeGroup: CodeGroup,
  code?: string,
  modifier?: string | string[],
): boolean => {
  if (!code) {
    return false;
  }

  for (let ind = 0, len = codeGroup.codes.length; ind < len; ind++) {
    if (!codeGroup.codes[ind].empty && compareNoCase(code, codeGroup.codes[ind].code)) {
      if (!modifier) {
        return true;
      }

      // code with modifier
      const { modifiers } = codeGroup.codes[ind];
      if (
        Array.isArray(modifier) &&
        modifier.length === modifiers?.length &&
        modifier.every((singleModifier) => modifierIsInList(modifiers, singleModifier))
      ) {
        return true;
      }

      if (typeof modifier === "string" && modifierIsInList(modifiers, modifier)) {
        return true;
      }
    }
  }

  return false;
};
